import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnChanges,
  inject,
  input,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Subscription, catchError, of, switchMap, timer } from 'rxjs';
import { provideUnscopedCompanyScope } from '@services/helpers/unscoped-company-scope';
import {
  PartnerDealGateResponse,
  PartnerDealGateResult,
  PartnerDealGatesProcessingResponse,
  PartnerDealGatesResponse,
} from '@interfaces/partners/responses/partner-deal-gates.response';
import { PartnersHttpService } from '@services/http/partners/partners-http.service';
import { SharedModule } from '@src/app/shared/shared.module';
import { VaultDivideByMilionAndFormatPipe } from '@src/app/shared/pipes/vault-divide-by-milion-and-format.pipe';

/** Texto e ícone de cada resultado, para o selo do gate e do conjunto. */
export const GATE_RESULT_LABELS: Record<PartnerDealGateResult, { text: string; icon: string }> = {
  Aprovado: { text: 'Atende', icon: 'pi-check-circle' },
  // "Não atende", e não "Reprovado": o gate barra, mas quem decide o deal é o time (06/10/2026).
  Reprovado: { text: 'Não atende', icon: 'pi-exclamation-triangle' },
  Pendente: { text: 'Pendente', icon: 'pi-clock' },
  SemLimite: { text: 'Sem limite definido', icon: 'pi-minus-circle' },
};

/**
 * Gates de elegibilidade do deal (card 3281, T05), na Visão geral da etapa **Gates**.
 *
 * Reunião de 02/10/2026: *"vai entrar a parte dos gates agora... já considerando os dados da
 * extração"*. Cada gate mostra o número da empresa (DFP padronizada, os mesmos números da aba Análise
 * econômica, card 3268), o limite da Parametrização e o resultado. Todo cálculo é do servidor
 * (`GET partners/deals/{id}/gates`); aqui só se formata.
 *
 * - Sem documento extraído: **Pendente**, nunca reprovado.
 * - Sem limite definido (decisão em aberto no card 3075): mostra o número e "Sem limite definido".
 * - Só o time interno chega aqui: Gates é etapa interna (card 3088).
 * - **Barrar não é rejeitar** (validação do Breno em 06/10/2026): gate fora do limite gera o aviso
 *   "este deal é barrado por N gates" no topo do painel. Nada é rejeitado automaticamente; rejeitar
 *   (com o motivo) ou seguir continua sendo decisão do time.
 * - **Ignora o escopo de empresas do TENANT** (card 3298), como as abas do deal: o backend calcula com
 *   o motor da DFP, que recusa empresa fora do escopo escolhido, e o painel mostraria "Pendente" para um
 *   deal que tem os números.
 * - **Documento processando não é "sem dados"** (card 3300): com documento em triagem ou extração, o
 *   painel avisa quantos estão sendo processados, acompanha o andamento a cada 2s e recarrega os gates
 *   sozinho quando termina.
 */
@Component({
  selector: 'app-deal-gates-panel',
  standalone: true,
  imports: [SharedModule],
  templateUrl: './deal-gates-panel.component.html',
  styleUrl: './deal-gates-panel.component.scss',
  providers: provideUnscopedCompanyScope([PartnersHttpService]),
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealGatesPanelComponent implements OnChanges {
  private readonly partnersHttp = inject(PartnersHttpService);
  private readonly destroyRef = inject(DestroyRef);

  /** Deal exibido. */
  readonly dealId = input.required<string>();

  /** Intervalo da consulta de andamento enquanto há documento processando (card 3300). */
  static readonly POLL_INTERVAL_MS = 2_000;

  /** Falhas seguidas da consulta de andamento que encerram o acompanhamento. */
  static readonly MAX_CONSECUTIVE_FAILURES = 3;

  /**
   * Teto do acompanhamento: 450 consultas de 2s = 15 minutos. Um arquivo preso na triagem (mensagem
   * perdida, Cog que não respondeu) deixaria o painel consultando para sempre; depois do teto, o aviso
   * oferece "Atualizar".
   */
  static readonly MAX_POLLS = 450;

  protected readonly gates = signal<PartnerDealGatesResponse | null>(null);
  protected readonly loading = signal(true);
  protected readonly error = signal<string | null>(null);
  protected readonly labels = GATE_RESULT_LABELS;

  /** Andamento mais recente do processamento; começa com o que veio junto dos gates. */
  protected readonly processing = signal<PartnerDealGatesProcessingResponse | null>(null);

  /** O acompanhamento automático parou (falhas seguidas ou teto de tempo); a tela oferece "Atualizar". */
  protected readonly isTrackingStopped = signal(false);

  /** Parou pelo teto de tempo, não por falha: o aviso diz que ainda está processando. */
  protected readonly tookTooLong = signal(false);

  private loadSubscription?: Subscription;
  private tracking?: Subscription;
  private consecutiveFailures = 0;
  private pollCount = 0;

  ngOnChanges(): void {
    this.load();
  }

  protected load(): void {
    this.loading.set(true);
    this.error.set(null);
    this.stopTracking();
    // Troca de deal com a carga anterior em andamento: a resposta antiga não pode sobrescrever a nova.
    this.loadSubscription?.unsubscribe();
    this.loadSubscription = this.partnersHttp
      .getDealGates(this.dealId())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: gates => {
          this.gates.set(gates);
          this.processing.set(gates?.processing ?? null);
          this.loading.set(false);
          if (gates?.processing?.isProcessing) this.trackProcessing();
        },
        error: err => {
          this.error.set(err?.errors?.[0]?.message ?? 'Não foi possível calcular os gates agora.');
          this.loading.set(false);
        },
      });
  }

  /**
   * Com documento processando, consulta só o andamento (endpoint leve, sem recalcular a DFP) a cada
   * `POLL_INTERVAL_MS`; quando termina, recarrega os gates uma vez, sem ação do usuário (card 3300).
   *
   * O `catchError` fica DENTRO do `switchMap`, como no `DfpEmptyStateComponent`: um erro de rede é só um
   * tick perdido e não encerra o `timer`. Três falhas seguidas encerram, para não virar consulta eterna
   * num endpoint quebrado.
   */
  private trackProcessing(): void {
    this.stopTracking();
    this.consecutiveFailures = 0;
    this.pollCount = 0;
    this.isTrackingStopped.set(false);
    this.tookTooLong.set(false);
    const dealId = this.dealId();

    this.tracking = timer(
      DealGatesPanelComponent.POLL_INTERVAL_MS,
      DealGatesPanelComponent.POLL_INTERVAL_MS
    )
      .pipe(
        switchMap(() =>
          this.partnersHttp.getDealGatesProcessing(dealId).pipe(catchError(() => of(null)))
        ),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(progress => this.applyProgress(progress));
  }

  /** Aplica uma resposta do andamento: atualiza a contagem, ou recarrega os gates quando terminou. */
  private applyProgress(progress: PartnerDealGatesProcessingResponse | null): void {
    this.pollCount += 1;
    if (this.pollCount >= DealGatesPanelComponent.MAX_POLLS && progress?.isProcessing !== false) {
      this.stopTracking();
      this.isTrackingStopped.set(true);
      this.tookTooLong.set(true);
      if (progress) this.processing.set(progress);
      return;
    }

    if (!progress) {
      this.consecutiveFailures += 1;
      if (this.consecutiveFailures >= DealGatesPanelComponent.MAX_CONSECUTIVE_FAILURES) {
        this.stopTracking();
        this.isTrackingStopped.set(true);
      }
      return;
    }

    this.consecutiveFailures = 0;
    this.processing.set(progress);
    if (!progress.isProcessing) this.load();
  }

  private stopTracking(): void {
    this.tracking?.unsubscribe();
    this.tracking = undefined;
  }

  /**
   * "1 documento da empresa está sendo processado." / "3 documentos da empresa estão sendo processados."
   * Triagem e extração somam: para quem olha o painel, os dois são "o documento ainda não chegou".
   */
  protected describeProcessing(p: PartnerDealGatesProcessingResponse): string {
    const total = p.triageCount + p.extractionCount;
    if (total > 1) return `${total} documentos da empresa estão sendo processados.`;
    if (total === 1) return '1 documento da empresa está sendo processado.';
    return 'Há documentos da empresa sendo processados.';
  }

  /** Selo do conjunto: "Barrado por N gate(s)" quando algo barra; senão o resultado do conjunto. */
  protected overallBadge(g: PartnerDealGatesResponse): {
    text: string;
    icon: string;
    tone: string;
  } {
    const blockingCount = g.blockedBy?.length ?? 0;
    if (blockingCount > 0) {
      return {
        text: `Barrado por ${blockingCount} ${blockingCount === 1 ? 'gate' : 'gates'}`,
        icon: 'pi-exclamation-triangle',
        tone: 'Reprovado',
      };
    }
    return { ...this.labels[g.result], tone: g.result };
  }

  /** "Faturamento" / "Faturamento e Endividamento". */
  protected joinGateNames(names: string[]): string {
    if (names.length <= 1) return names[0] ?? '';
    return `${names.slice(0, -1).join(', ')} e ${names[names.length - 1]}`;
  }

  /** "≥ R$ 50,0 mi" / "≤ 3,0x" / "—". */
  protected describeLimit(gate: PartnerDealGateResponse): string {
    if (gate.limit == null) return '—';
    const sign = gate.comparison === 'min' ? '≥' : '≤';
    return `${sign} ${this.format(gate.limit, gate)}`;
  }

  protected describeValue(gate: PartnerDealGateResponse): string {
    return gate.value == null ? '—' : this.format(gate.value, gate);
  }

  private format(value: number, gate: PartnerDealGateResponse): string {
    return formatGateNumber(value, gate);
  }
}

/** "R$ 50,0 mi" / "3,0x": formato do número de um gate, no painel e no resumo da Visão geral (card 3380). */
export function formatGateNumber(
  value: number,
  gate: Pick<PartnerDealGateResponse, 'unit'>
): string {
  if (gate.unit === 'x') return `${value.toFixed(1).replace('.', ',')}x`;
  return `R$ ${new VaultDivideByMilionAndFormatPipe().transform(value, 1)} mi`;
}
